/**
 * Admin configuration types
 * System-wide settings managed by administrators
 */

import type { UserPreferences } from './preferences.types';
import { FILE_CONSTRAINTS } from '@/utils/constants';

/**
 * Application branding configuration
 */
export interface BrandingConfig {
  // Header
  headerTitle: string;
  headerSubtitle?: string;
  headerLogoUrl?: string;

  // Browser/Page
  pageTitle: string;
  faviconUrl?: string;

  // Theme colors
  primaryColor?: string;
  accentColor?: string;
}

/**
 * Entra ID (Azure AD) group to application role mapping
 */
export interface EntraGroupMapping {
  id: string; // Unique ID for this mapping
  groupId: string; // Azure AD group object ID
  groupName: string; // Friendly name for display
  role: 'admin' | 'power_user' | 'user'; // Application role
}

/**
 * Authentication database configuration
 * Used for user credentials, sessions, and admin config
 * This is separate from chat history storage
 */
export interface AuthDatabaseConfig {
  // Backend API URL for authentication
  backendUrl: string;
  // Database type (future: could support multiple backends)
  type: 'postgresql';
  // Connection status
  enabled: boolean;
  // Entra ID group to role mappings (only used when Entra auth is enabled)
  entraGroupMappings?: EntraGroupMapping[];
}

/**
 * Chat history storage configuration for all users
 * Frontend only communicates with backend API
 * Backend handles all database connections internally
 */
export interface AdminStorageConfig {
  // No configuration needed - backend handles everything
  // This interface kept for backward compatibility
  _note: string;
}

/**
 * User preference overrides and defaults
 */
export interface UserDefaultsConfig {
  // Default preferences for new users
  defaults: Partial<UserPreferences>;

  // Which preferences users can override
  allowOverride: {
    theme: boolean;
    fontSize: boolean;
    messageGrouping: boolean;
    showTimestamps: boolean;
    compactMode: boolean;
    sendOnEnter: boolean;
    showTypingIndicator: boolean;
    enableSounds: boolean;
    keepFilesAcrossMessages: boolean;
    temperature: boolean;
    maxTokens: boolean;
    enableCitations: boolean;
    enableFileUpload: boolean;
    highContrast: boolean;
    reducedMotion: boolean;
    screenReaderOptimized: boolean;
  };
}

/**
 * Agent control configuration
 */
export interface AgentControlConfig {
  // Enable agent selection feature
  enableAgentSelection: boolean;

  // Default agent for all users (by agent ID)
  defaultAgentId?: string;

  // Per-user agent assignments (future)
  userAgentAssignments?: Record<string, string>; // userId -> agentId
}

/**
 * Welcome message configuration
 */
export interface WelcomeMessageConfig {
  // Use custom message or fall back to branding title
  useCustomMessage: boolean;

  // Welcome title (supports markdown)
  title: string;

  // Welcome subtitle (supports markdown)
  subtitle: string;
}

/**
 * Conversation retention configuration
 */
export interface RetentionConfig {
  /** Number of days deleted conversations are kept in trash before permanent removal. Range: 1–365. */
  conversationTrashDays: number;
}

/**
 * Per-deployment file upload limits. Admin-configurable via the
 * "File Uploads" tab in the Admin Configuration Console. Consumed by
 * `InputArea` (pre-attach validation + `accept=` attribute) and
 * `parseFile()` (parse-time guard).
 */
export interface UploadLimitsConfig {
  /** Maximum size per file in megabytes. Range: 1–500. */
  maxFileSizeMB: number;
  /** Maximum number of files per message. Range: 1–10. */
  maxFiles: number;
  /** File extensions users are allowed to attach (e.g. ".pdf"). */
  allowedExtensions: string[];
}

/**
 * Complete admin configuration
 */
export interface AdminConfig {
  branding: BrandingConfig;
  authDatabase: AuthDatabaseConfig;
  storage: AdminStorageConfig;
  userDefaults: UserDefaultsConfig;
  agentControl: AgentControlConfig;
  welcomeMessage: WelcomeMessageConfig;
  uploadLimits: UploadLimitsConfig;
  retention: RetentionConfig;

  // Future expansion
  features?: {
    ragEnabled: boolean;
    fileUploadEnabled: boolean;
    agentManagementEnabled: boolean;
    enableEntraAuth: boolean;
    enableDatabaseAuth: boolean;
  };
}

/**
 * Default admin configuration
 */
export const DEFAULT_ADMIN_CONFIG: AdminConfig = {
  branding: {
    headerTitle: 'Stellar M365 Chatbot',
    headerSubtitle: 'AI-Powered Assistant',
    pageTitle: 'Stellar M365 Chatbot',
  },
  authDatabase: {
    // Use backend URL from environment, fallback to localhost
    backendUrl: import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000/api',
    type: 'postgresql',
    enabled: true,
    entraGroupMappings: [],
  },
  storage: {
    _note: 'Chat history managed by backend - no frontend configuration needed',
  },
  userDefaults: {
    defaults: {
      theme: 'system',
      fontSize: 'medium',
      messageGrouping: true,
      showTimestamps: true,
      compactMode: false,
      sendOnEnter: true,
      showTypingIndicator: true,
      enableSounds: false,
      keepFilesAcrossMessages: false,
      temperature: 0.7,
      maxTokens: 4000,  // Increased from 1000 for longer responses
      enableCitations: true,
      enableFileUpload: true,
      highContrast: false,
      reducedMotion: false,
      screenReaderOptimized: false,
    },
    allowOverride: {
      theme: true,
      fontSize: true,
      messageGrouping: true,
      showTimestamps: true,
      compactMode: true,
      sendOnEnter: true,
      showTypingIndicator: true,
      enableSounds: true,
      keepFilesAcrossMessages: true,
      temperature: true,
      maxTokens: true,
      enableCitations: true,
      enableFileUpload: true,
      highContrast: true,
      reducedMotion: true,
      screenReaderOptimized: true,
    },
  },
  agentControl: {
    enableAgentSelection: true,
    defaultAgentId: undefined,
  },
  welcomeMessage: {
    useCustomMessage: false,
    title: 'Welcome to Stellar Assistant',
    subtitle:
      'Get started by selecting a suggested prompt or type your own question',
  },
  uploadLimits: {
    maxFileSizeMB: 100,
    maxFiles: 5,
    // Every supported type enabled by default; admins disable individual
    // types via the Upload Limits tab.
    allowedExtensions: [...FILE_CONSTRAINTS.SUPPORTED_EXTENSIONS],
  },
  retention: { conversationTrashDays: 30 },
  features: {
    ragEnabled: true,
    fileUploadEnabled: true,
    agentManagementEnabled: true,
    enableEntraAuth: true,
    enableDatabaseAuth: true,
  },
};

/**
 * Admin context type
 */
export interface AdminContextType {
  config: AdminConfig;
  updateBranding: (branding: Partial<BrandingConfig>) => void;
  updateAuthDatabase: (authDatabase: Partial<AuthDatabaseConfig>) => void;
  updateStorage: (storage: Partial<AdminStorageConfig>) => void;
  updateUserDefaults: (userDefaults: Partial<UserDefaultsConfig>) => void;
  updateAgentControl: (agentControl: Partial<AgentControlConfig>) => void;
  updateWelcomeMessage: (
    welcomeMessage: Partial<WelcomeMessageConfig>
  ) => void;
  updateUploadLimits: (uploadLimits: Partial<UploadLimitsConfig>) => void;
  updateFeatures: (features: Partial<AdminConfig['features']>) => void;
  resetConfig: () => Promise<void>;
  exportConfig: () => string;
  importConfig: (data: string) => void;
  isAdmin: boolean;
  isLoading: boolean;
}

/**
 * Admin role for access control
 */
export interface AdminRole {
  userId: string;
  email: string;
  isAdmin: boolean;
  grantedAt: Date;
  grantedBy?: string;
}
